import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signIn, signUp } from "../../services/auth/auth";
import logo from "../../assets/logo-cloud.png";
import { Mail, Lock, LockOpen, Phone, User, EyeOff, Eye } from "lucide-react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  Snackbar,
  Select,
  MenuItem,
} from "@mui/material";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");

  const countryCodes = [
    { code: "+1", country: "US" },
    { code: "+44", country: "UK" },
    { code: "+91", country: "IN" },
    { code: "+61", country: "AU" },
    { code: "+86", country: "CN" },
    { code: "+81", country: "JP" },
    { code: "+49", country: "DE" },
  ];

  const handleCloseError = () => {
    setError("");
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    try {
      setLoading(true);
      const session = await signIn(email, password);
      if (session && typeof session.AccessToken !== "undefined") {
        localStorage.setItem("accessToken", session.AccessToken);
        if (localStorage.getItem("accessToken")) {
          window.location.href = "/";
        } else {
          console.error("Session token was not set properly.");
        }
      } else {
        console.error("SignIn session or AccessToken is undefined.");
      }
    } catch (error) {
      const errorMessage =
        error.message || "Failed to Sign in. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!email.endsWith("@visteon.com")) {
      const errorMessage =
        "Sign up is allowed only for @visteon.com email addresses.";
      setError(errorMessage);
      return;
    } else {
      setError("");
    }
    if (password !== confirmPassword) {
      const errorMessage = "Passwords do not match";
      setError(errorMessage);
      return;
    }
    try {
      setLoading(true);
      await signUp(email, password, firstName, lastName, countryCode + phoneNo);
      setSuccess(true);
      setTimeout(() => {
        navigate("/confirm", { state: { email } });
      }, 2000);
    } catch (error) {
      const errorMessage =
        error.message || "Failed to create Account. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowPassword1 = () => setShowPassword1((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handlePassChange = (e) => {
    e.preventDefault();
    navigate("/resetPassword");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#003366",
        fontFamily: `"Source Sans Pro", "Amazon Ember", "Raleway", sans-serif`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          boxShadow: "inset 0 0 50px rgba(0, 0, 0, 0.5)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        component="img"
        src={logo}
        alt="Logo"
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          width: "100px",
        }}
      />
      <Paper
        sx={{
          width: isSignUp ? "600px" : "400px",
          padding: "30px 40px",
          borderRadius: "8px",
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          transition: "width 0.3s ease, transform 0.3s ease, opacity 0.3s ease",
          transform: isSignUp ? "translateX(0)" : "translateX(0)",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: "#003366",
            fontWeight: 600,
            fontFamily: `"Raleway", sans-serif`,
          }}
        >
          {isSignUp ? "Sign Up" : "Login"}
        </Typography>
        <Typography sx={{ color: "#003366" }}>
          {isSignUp ? "Fill the details" : "Sign in to your account"}
        </Typography>
        {loading && (
         <Box>
         <CircularProgress sx={{ color: '#003366' }} />
       </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
            gap: 2,
          }}
        >
          <form
            onSubmit={isSignUp ? handleSignUp : handleSignIn}
            className={isSignUp ? "grid grid-cols-2 gap-4" : "flex flex-col"}
            style={{ width: "100%" }}
          >
            {!isSignUp && (
              <>
                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />

                <TextField
                  id="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock className="h-6 w-6 text-[#003366]" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-[#003366]" />
                            ) : (
                              <Eye className="h-4 w-4  text-[#003366]" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
                <Link
                  onClick={handlePassChange}
                  sx={{
                    marginTop: "20px",
                    fontFamily: `"Raleway", sans-serif`,
                    cursor: "pointer",
                    color: "#003366",
                  }}
                >
                  Forgot Password?
                </Link>
              </>
            )}

            {isSignUp && (
              <>
                <TextField
                  id="firstName"
                  label="First Name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
                <TextField
                  id="lastName"
                  label="Last Name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail className="h-6 w-6 text-[#003366]" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
                <TextField
                  id="phoneNo"
                  label="Phone Number"
                  type="text"
                  value={phoneNo}
                  onChange={(e) => setPhoneNo(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone className="h-6 w-6 text-[#003366]" />
                        <div className="flex items-center pl-3">
                          <Select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            variant="standard"
                            disableUnderline
                            sx={{
                              maxWidth: "90px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "0 !important",
                                fontSize: "1rem", // Match default input font size
                              },
                              "& .MuiSelect-icon": {
                                display: "none",
                              },
                            }}
                          >
                            {countryCodes.map((country) => (
                              <MenuItem
                                key={country.code}
                                value={country.code}
                                sx={{
                                  minWidth: "80px",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {`${country.code}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
                <TextField
                  id="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOpen className="h-6 w-6 text-[#003366]" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-[#003366]" />
                            ) : (
                              <Eye className="h-4 w-4 text-[#003366]" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
                <TextField
                  id="confirmPassword"
                  label="Confirm Password"
                  type={showPassword1 ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock className="h-6 w-6 text-[#003366]" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword1}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            size="small"
                          >
                            {showPassword1 ? (
                              <EyeOff className="h-4 w-4 text-[#003366]" />
                            ) : (
                              <Eye className="h-4 w-4 text-[#003366]" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#003366",
                      },
                    },
                    backgroundColor: "#F0F8FF",
                    borderRadius: 1,
                  }}
                />
              </>
            )}
            <Box
              sx={{
                textAlign: "center",
                mt: 2,
                gridColumn: isSignUp ? "span 2" : "auto",
              }}
            >
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                variant="contained"
                sx={{
                  backgroundColor: "#FFD700",
                  color: "#003366",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#FFC300",
                  },
                }}
              >
                {isSignUp ? (
                  <Typography
                    sx={{
                      color: "#003366",
                      fontFamily: ` "Raleway", sans-serif`,
                    }}
                  >
                    Sign Up
                  </Typography>
                ) : (
                  <Typography
                    sx={{ color: "#555", fontFamily: ` "Raleway", sans-serif` }}
                  >
                    Login
                  </Typography>
                )}
              </Button>
            </Box>
          </form>
        </Box>
        <Typography
          variant="body2"
          sx={{
            marginTop: "20px",
            color: "#003366",
            fontFamily: ` "Raleway", sans-serif`,
            cursor: "pointer",
          }}
          onClick={() => setIsSignUp(!isSignUp)}
        >
          {isSignUp
            ? "Already have an account? Sign In"
            : "Don't have an account? Create one"}
        </Typography>
      </Paper>
{/* Footer */}
<Box
  sx={{
    position: "absolute",
    bottom: 12,
    textAlign: "center",
    width: "100%",
  }}
>
  <Typography
    variant="caption"
    sx={{
      color: "rgba(255, 255, 255, 0.7)",
      fontFamily: `"Raleway", sans-serif`,
      fontSize: "0.75rem",
      letterSpacing: "0.5px",
    }}
  >
    © {new Date().getFullYear()} Visteon Corporation. All rights reserved.
  </Typography>
</Box>
      
      {/* Success Message */}
      <Snackbar
        open={success}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Account created successfully! Confirm email in further step to start
          using
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}